// cSpell:ignore Gemeenschappelijke Kosten kosten Privatieve privé
// The kinds of cost a settlement's tables are split into (Lewis,
// 2026-10-18), on the terms of the provisions step and the lease: the
// common costs (a building's) first, then the private ones (a unit's own),
// each under a light grey bar with what it is in its "?". The summary's
// party cards and the confirmed settlement's "Kosten" (Lewis, 2026-10-19)
// share them, so a cost is of the same kind in both.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { FontWeights } from "@rentiohq/web-theme";
import React from "react";
import styled from "styled-components";
import { isUnitBreakdownKey } from "../../../../buildingSettlement.utils";
import { Invoice } from "../../../../types";
import { InfoTip } from "./InfoTip";
import { CELL_PADDING } from "./provisionColumns";
import { byCatKey } from "./summaryCards.utils";

export type TCostKind = "common" | "private";

export const COST_KIND_ORDER: TCostKind[] = ["common", "private"];

export const COST_KIND_KEYS: Record<
  TCostKind,
  { label: string; help: string }
> = {
  common: {
    label: "settlement.summary.kind.common",
    help: "costs.list.column.type.help.common",
  },
  private: {
    label: "settlement.summary.kind.private",
    help: "costs.list.column.type.help.private",
  },
};

// The kind of a breakdown key ("Elevator", "Water (privé)").
export const costKindOf = (key: string): TCostKind =>
  isUnitBreakdownKey(key) ? "private" : "common";

// The kind of a cost: of its breakdown key, as the summary groups it.
export const costKindOfInvoice = (invoice: Invoice): TCostKind =>
  costKindOf(byCatKey(invoice));

// "Gemeenschappelijke kosten", "Privatieve kosten".
export const costKindLabel = (kind: TCostKind): string =>
  getLocalizedText(COST_KIND_KEYS[kind].label);

// A kind's row: a compact light grey band over the full width, square at
// its ends. Its own padding wins over a table wrapper's (`&&`).
const GroupRow = styled.tr`
  && td {
    padding: 6px ${CELL_PADDING};
    border-radius: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral20};
    background: ${({ theme }) => theme.colors.neutral10};
  }
`;

const GroupHead = styled.div`
  display: flex;
  align-items: center;
  .group-label {
    font-weight: ${FontWeights.Medium};
  }
`;

// The bar of a kind over a table's `colSpan` columns, its term in a "?".
export const CostKindBar: React.FC<{ kind: TCostKind; colSpan: number }> = ({
  kind,
  colSpan,
}) => {
  const label = costKindLabel(kind);
  return (
    <GroupRow>
      <td colSpan={colSpan}>
        <GroupHead>
          <span className="group-label">{label}</span>
          <InfoTip
            help
            rows={[
              { label, value: getLocalizedText(COST_KIND_KEYS[kind].help) },
            ]}
          />
        </GroupHead>
      </td>
    </GroupRow>
  );
};
